import React, { useState, useCallback } from "react";
import Navbar from "../nav/navbar";
import Nextstep from "../nextStep/nextstep";
import Footer from "../footer/footer";
import { FaArrowRight } from "react-icons/fa";
import { motion, easeInOut } from "framer-motion";
import toast, { Toaster } from "react-hot-toast";
import { Helmet } from "react-helmet";

// ─── Constants ────────────────────────────────────────────────────────────────

const API_URL = "https://connectedpay.haodapay.com/api/v1/leadstoreone";

const INVESTOR_TYPES = [
    { value: "individual", label: "Individual Investor" },
    { value: "angel", label: "Angel Investor" },
    { value: "vc", label: "Venture Capital" },
    { value: "pe", label: "Private Equity" },
    { value: "family_office", label: "Family Office" },
    { value: "corporate", label: "Corporate Investor" },
    { value: "other", label: "Other" },
];

const INVESTMENT_RANGES = [
    { value: "50cr", label: "₹50 Crore" },
    { value: "above_50cr", label: "Above ₹50 Crore" },
];

const CONTACT_METHODS = [
    { value: "email", label: "Email" },
    { value: "phone", label: "Phone" },
    { value: "whatsapp", label: "WhatsApp" },
];

const PURPOSE_ITEMS = [
    "Strengthen regulatory compliance and governance.",
    "Meet RBI licensing and capital requirements.",
    "Expand our payment technology platform.",
    "Enhance security and risk management systems.",
    "Scale merchant acquisition across India.",
    "Build a sustainable and compliant payment ecosystem.",
];

const WHY_INVEST_ITEMS = [
    {
        title: "Growing Digital Payments Market",
        desc: "India is one of the world's fastest-growing digital payment markets, with increasing adoption across businesses of every size.",
    },
    {
        title: "Experienced Team",
        desc: "Our team combines expertise in fintech, payment processing, technology, and business operations.",
    },
    {
        title: "Scalable Business Model",
        desc: "Our platform is designed to support businesses ranging from startups and SMEs to large enterprises.",
    },
    {
        title: "Long-Term Growth Vision",
        desc: "We are focused on building a compliant, technology-driven organization with sustainable growth rather than pursuing short-term gains.",
    },
];

const PARTNER_TYPES = [
    "Strategic Investors",
    "Venture Capital Funds",
    "Private Equity Funds",
    "Family Offices",
    "Financial Institutions",
    "Payment Industry Partners",
];

// ─── Validation ───────────────────────────────────────────────────────────────

const INITIAL_FORM_STATE = {
    fullname: "",
    business_name: "",
    mobile_number: "",
    email_address: "",
    investor_type: "",
    country: "",
    invest_range: "",
    interest: "",
    contact_method: "",
};

function validateForm(fields) {
    const errors = {};

    // Full name validation
    if (!fields.fullname?.trim()) {
        errors.fullname = "Full name is required.";
    } else if (fields.fullname.trim().length > 255) {
        errors.fullname = "Full name must not exceed 255 characters.";
    }

    // Business name validation
    if (!fields.business_name?.trim()) {
        errors.business_name = "Business name is required.";
    } else if (fields.business_name.trim().length > 255) {
        errors.business_name = "Business name must not exceed 255 characters.";
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!fields.email_address?.trim()) {
        errors.email_address = "Email address is required.";
    } else if (!emailRegex.test(fields.email_address)) {
        errors.email_address = "Enter a valid email address.";
    } else if (fields.email_address.length > 255) {
        errors.email_address = "Email must not exceed 255 characters.";
    }

    // Mobile number validation
    if (!fields.mobile_number?.trim()) {
        errors.mobile_number = "Mobile number is required.";
    } else if (fields.mobile_number.trim().length > 20) {
        errors.mobile_number = "Mobile number must not exceed 20 characters.";
    } else if (fields.mobile_number.trim().length < 10) {
        errors.mobile_number = "Enter a valid mobile number (minimum 10 digits).";
    }

    // Country validation
    if (!fields.country?.trim()) {
        errors.country = "Country is required.";
    } else if (fields.country.trim().length > 255) {
        errors.country = "Country must not exceed 255 characters.";
    }

    // Investor type validation
    if (!fields.investor_type) {
        errors.investor_type = "Please select an investor type.";
    } else if (fields.investor_type.length > 255) {
        errors.investor_type = "Investor type must not exceed 255 characters.";
    }

    // Investment range validation
    if (!fields.invest_range) {
        errors.invest_range = "Please select an investment range.";
    } else if (fields.invest_range.length > 255) {
        errors.invest_range = "Investment range must not exceed 255 characters.";
    }

    // Interest validation
    if (!fields.interest?.trim()) {
        errors.interest = "Please tell us about your interest.";
    } else if (fields.interest.trim().length > 255) {
        errors.interest = "Interest must not exceed 255 characters.";
    }

    // Contact method validation
    if (!fields.contact_method) {
        errors.contact_method = "Please select a preferred contact method.";
    } else if (fields.contact_method.length > 255) {
        errors.contact_method = "Contact method must not exceed 255 characters.";
    }

    return errors;
}

// ─── Custom Hook ──────────────────────────────────────────────────────────────

function useLeadForm() {
    const [fields, setFields] = useState(INITIAL_FORM_STATE);
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleChange = useCallback((e) => {
        const { name, value } = e.target;
        setFields((prev) => ({ ...prev, [name]: value }));
        setErrors((prev) => ({ ...prev, [name]: undefined }));
    }, []);

    const handleSubmit = useCallback(async () => {
        const validationErrors = validateForm(fields);
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            // Scroll to first error
            const firstError = document.querySelector('[aria-invalid="true"]');
            if (firstError) {
                firstError.scrollIntoView({ behavior: "smooth", block: "center" });
            }
            return;
        }

        setIsSubmitting(true);
        try {
            const response = await fetch(API_URL, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                },
                body: JSON.stringify(fields),
            });

            const result = await response.json();

            if (response.ok) {
                toast.success("Thank you for your interest! We'll be in touch shortly.");
                setFields(INITIAL_FORM_STATE);
                setErrors({});
            } else {
                // Handle validation errors from backend
                if (result?.errors) {
                    const errorMessages = Object.values(result.errors).flat().join(" ");
                    toast.error(errorMessages);
                } else if (result?.message) {
                    toast.error(result.message);
                } else {
                    toast.error("Failed to submit. Please try again.");
                }
            }
        } catch (error) {
            console.error("Submission error:", error);
            toast.error(
                "Something went wrong. Please check your connection and try again.",
            );
        } finally {
            setIsSubmitting(false);
        }
    }, [fields]);

    return { fields, errors, isSubmitting, handleChange, handleSubmit };
}


// ─── Sub-components ───────────────────────────────────────────────────────────

function FieldError({ message }) {
    if (!message) return null;
    return <p role="alert" className="text-red-500 text-sm mt-1">{message}</p>;
}

function InvestorForm() {
    const { fields, errors, isSubmitting, handleChange, handleSubmit } = useLeadForm();

    const inputClass =
        "w-full px-4 py-3 text-sm border rounded-lg focus:outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600 transition-all";

    return (
        <div className="bg-white rounded-2xl shadow-2xl p-6 md:p-8 text-black">
            <h3 className="text-xl md:text-2xl font-semibold mb-6 text-center">
                Become a Strategic Investor
            </h3>

            <div className="space-y-5" role="form" aria-label="Investor interest form">
                {/* Name & Business */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                        <input
                            id="fullname"
                            type="text"
                            name="fullname"
                            placeholder="Full Name"
                            value={fields.fullname}
                            onChange={handleChange}
                            className={`${inputClass} ${errors.fullname ? 'border-red-400' : 'border-gray-300'}`}
                            aria-invalid={!!errors.fullname}
                            autoComplete="name"
                        />
                        <FieldError message={errors.fullname} />
                    </div>

                    <div>
                        <input
                            id="business_name"
                            type="text"
                            name="business_name"
                            placeholder="Business Name"
                            value={fields.business_name}
                            onChange={handleChange}
                            className={`${inputClass} ${errors.business_name ? 'border-red-400' : 'border-gray-300'}`}
                            aria-invalid={!!errors.business_name}
                            autoComplete="organization"
                        />
                        <FieldError message={errors.business_name} />
                    </div>
                </div>

                {/* Email & Mobile */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                        <input
                            id="email_address"
                            type="email"
                            name="email_address"
                            placeholder="Email Address"
                            value={fields.email_address}
                            onChange={handleChange}
                            className={`${inputClass} ${errors.email_address ? 'border-red-400' : 'border-gray-300'}`}
                            aria-invalid={!!errors.email_address}
                            autoComplete="email"
                        />
                        <FieldError message={errors.email_address} />
                    </div>

                    <div>
                        <input
                            id="mobile_number"
                            type="tel"
                            name="mobile_number"
                            placeholder="Mobile Number"
                            value={fields.mobile_number}
                            onChange={handleChange}
                            inputMode="tel"
                            className={`${inputClass} ${errors.mobile_number ? 'border-red-400' : 'border-gray-300'}`}
                            aria-invalid={!!errors.mobile_number}
                            autoComplete="tel"
                        />
                        <FieldError message={errors.mobile_number} />
                    </div>
                </div>

                {/* Country & Investor Type */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                        <input
                            id="country"
                            type="text"
                            name="country"
                            placeholder="Country"
                            value={fields.country}
                            onChange={handleChange}
                            className={`${inputClass} ${errors.country ? 'border-red-400' : 'border-gray-300'}`}
                            aria-invalid={!!errors.country}
                            autoComplete="country-name"
                        />
                        <FieldError message={errors.country} />
                    </div>

                    <div>
                        <select
                            id="investor_type"
                            name="investor_type"
                            value={fields.investor_type}
                            onChange={handleChange}
                            className={`${inputClass} ${errors.investor_type ? 'border-red-400' : 'border-gray-300'}`}
                            aria-invalid={!!errors.investor_type}
                        >
                            <option value="">Investor Type</option>
                            {INVESTOR_TYPES.map(({ value, label }) => (
                                <option key={value} value={value}>{label}</option>
                            ))}
                        </select>
                        <FieldError message={errors.investor_type} />
                    </div>
                </div>

                {/* Investment Range */}
                <div>
                    <select
                        id="invest_range"
                        name="invest_range"
                        value={fields.invest_range}
                        onChange={handleChange}
                        className={`${inputClass} ${errors.invest_range ? 'border-red-400' : 'border-gray-300'}`}
                        aria-invalid={!!errors.invest_range}
                    >
                        <option value="">Investment Range</option>
                        {INVESTMENT_RANGES.map(({ value, label }) => (
                            <option key={value} value={value}>{label}</option>
                        ))}
                    </select>
                    <FieldError message={errors.invest_range} />
                </div>

                {/* Interest */}
                <div>
                    <textarea
                        id="interest"
                        name="interest"
                        placeholder="Tell Us About Your Interest"
                        value={fields.interest}
                        onChange={handleChange}
                        rows={3}
                        className={`${inputClass} resize-y min-h-[100px] ${errors.interest ? 'border-red-400' : 'border-gray-300'}`}
                        aria-invalid={!!errors.interest}
                    />
                    <FieldError message={errors.interest} />
                </div>

                {/* Contact Method */}
                <fieldset>
                    <legend className="text-sm font-medium mb-2 text-gray-700">Preferred Contact Method</legend>
                    <div className="flex flex-wrap gap-4">
                        {CONTACT_METHODS.map(({ value, label }) => (
                            <label key={value} className="flex items-center gap-2 cursor-pointer text-sm">
                                <input
                                    type="radio"
                                    name="contact_method"
                                    value={value}
                                    checked={fields.contact_method === value}
                                    onChange={handleChange}
                                    className="accent-blue-600 w-4 h-4"
                                />
                                {label}
                            </label>
                        ))}
                    </div>
                    <FieldError message={errors.contact_method} />
                </fieldset>

                <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-70 disabled:cursor-not-allowed text-white font-semibold py-3.5 rounded-xl transition text-base mt-2"
                    aria-busy={isSubmitting}
                >
                    {isSubmitting ? "Submitting…" : "Submit Interest"}
                </button>
            </div>
        </div>
    );
}

// ─── Animation Variants ───────────────────────────────────────────────────────

const fadeUpVariant = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: easeInOut } },
};

// ─── Main Component ───────────────────────────────────────────────────────────

const InvestmentProposal = () => {
    return (
        <>
            <Toaster position="top-right" reverseOrder={false} toastOptions={{ style: { zIndex: 9999 } }} />

            <Helmet>
                <meta charSet="utf-8" />
                <title>Strategic Investment Proposal | Haoda Pay</title>
                <meta name="description" content="Haoda Pay invites strategic investors to partner with us as we pursue the RBI Payment Aggregator & Payment Gateway (PAPG) License." />
                <meta name="keywords" content="Strategic investment, RBI PAPG license, fintech investors, Haoda Pay" />
            </Helmet>

            <Navbar />

            {/* Hero Section */}
            <div className="relative min-h-[90vh] md:min-h-screen flex items-center overflow-hidden mt-16">
                <div className="absolute inset-0">
                    <video
                        loop
                        autoPlay
                        muted
                        playsInline
                        aria-hidden="true"
                        className="w-full h-full object-cover"
                    >
                        <source src="/images/Web 800_1.mp4" type="video/mp4" />
                    </video>
                </div>

                {/* Overlay */}
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_80%,rgba(142,104,255,0.45)_0%,transparent_45%),radial-gradient(circle_at_90%_20%,rgba(255,220,120,0.55)_0%,transparent_40%),linear-gradient(90deg,#f7f7ff_0%,#f4efff_45%,#fff6dd_100%)]" />

                <div className="relative container mx-auto px-4 py-12 lg:py-20">
                    <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
                        {/* Left Content */}
                        <div className="text-white text-center lg:text-left max-w-2xl mx-auto lg:mx-0">
                            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6 text-black">
                                Strategic Investment Proposal
                            </h1>
                            <p className="text-lg md:text-xl opacity-95 mb-8 text-black">
                                Haoda Pay is inviting visionary investors to partner with us as we pursue the Reserve Bank of India (RBI) Payment Aggregator & Payment Gateway (PAPG) License. This strategic investment opportunity is designed for investors looking to participate in India's rapidly expanding digital payments ecosystem.
                            </p>

                            <div className="flex flex-col sm:flex-row gap-8 justify-center lg:justify-start mb-10 text-black">
                                <div>
                                    <p className="text-xs uppercase tracking-widest opacity-75">Seeking Investment</p>
                                    <p className="text-3xl font-bold">₹50 Crore</p>
                                </div>
                                <div>
                                    <p className="text-xs uppercase tracking-widest opacity-75">Equity Offered</p>
                                    <p className="text-3xl font-bold">25%</p>
                                </div>
                            </div>

                            <a href="#investor-form" className="inline-block">
                                <button className="bg-[#4E2AAD] text-white hover:bg-[#40228F] font-semibold px-10 py-4 rounded-2xl flex items-center gap-3 text-lg mx-auto transition">
                                    Become a Strategic Investor
                                    <FaArrowRight />
                                </button>
                            </a>
                        </div>

                        {/* Form */}
                        <div id="investor-form" className="lg:ml-auto max-w-lg mx-auto w-full">
                            <InvestorForm />
                        </div>
                    </div>
                </div>
            </div>

            {/* Vision & Mission */}
            <div className="py-16 md:py-24 bg-gray-50">
                <div className="container mx-auto px-4">
                    <motion.div
                        variants={fadeUpVariant}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        className="max-w-4xl mx-auto text-center"
                    >
                        <h2 className="text-3xl md:text-4xl font-bold mb-8">Building the Future with Our Vision & Mission</h2>
                        <p className="text-gray-600 text-lg leading-relaxed">
                            Haoda Payment Solutions Pvt. Ltd. is an Indian fintech company focused on providing secure and reliable payment solutions for businesses of all sizes. Our mission is to simplify digital transactions through innovative technology, strong compliance practices, and customer-centric services.
                        </p>
                        <p className="text-gray-600 text-lg leading-relaxed">
                            Our solutions are designed to help businesses collect, process, and manage payments efficiently while adapting to the evolving needs of India's digital economy.
                        </p>
                    </motion.div>
                </div>
            </div>

            {/* Purpose of Investment */}
            <div className="py-16 md:py-24">
                <div className="container mx-auto px-4">
                    <motion.div
                        variants={fadeUpVariant}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        className="max-w-4xl mx-auto"
                    >
                        <h2 className="text-3xl md:text-4xl font-bold text-center mb-6">Purpose of the Investment</h2>
                        <p className="text-center text-gray-600 text-lg mb-10">Obtaining an RBI Payment Aggregator License is an important milestone in our growth roadmap. The investment will enable us to:</p>

                        <ul className="grid sm:grid-cols-2 gap-5">
                            {PURPOSE_ITEMS.map((item) => (
                                <li key={item} className="flex items-start gap-3 bg-white rounded-2xl shadow p-6">
                                    <span className="mt-1.5 w-2 h-2 rounded-full bg-blue-600 flex-shrink-0" />
                                    <span className="text-gray-700">{item}</span>
                                </li>
                            ))}
                        </ul>
                        <p className="text-center text-gray-600 text-lg mt-4">
                            Our objective is to create long-term value by establishing a trusted payment infrastructure that supports businesses across multiple industries.
                        </p>
                    </motion.div>
                </div>
            </div>

            {/* Why Invest */}
            <div className="bg-gray-800 text-white py-16 md:py-24">
                <div className="container mx-auto px-4">
                    <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">Why Invest in Haoda Pay?</h2>
                    <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-8">
                        {WHY_INVEST_ITEMS.map(({ title, desc }, index) => (
                            <motion.div
                                key={title}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: index * 0.1 }}
                                className="bg-gray-900 p-8 rounded-3xl border border-gray-700 hover:border-blue-500 transition-all"
                            >
                                <h3 className="text-2xl font-semibold mb-4 text-blue-400">{title}</h3>
                                <p className="text-gray-300 leading-relaxed">{desc}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Partner Types */}
            <div className="py-16 md:py-24 bg-white">
                <div className="container mx-auto px-4">
                    <motion.div variants={fadeUpVariant} initial="hidden" whileInView="visible" viewport={{ once: true }}>
                        <h2 className="text-3xl md:text-4xl font-bold text-center mb-6">Looking for Long-Term Strategic Partners</h2>
                        <p className="text-center text-xl text-gray-600 mb-12 max-w-3xl mx-auto">
                            We welcome discussions with investors who have experience in fintech, banking...
                        </p>

                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
                            {PARTNER_TYPES.map((type) => (
                                <div
                                    key={type}
                                    className="bg-white border border-gray-200 rounded-2xl p-8 text-center hover:shadow-xl hover:border-blue-200 transition-all duration-300"
                                >
                                    <p className="font-semibold text-lg">{type}</p>
                                </div>
                            ))}
                        </div>
                    </motion.div>
                </div>
            </div>

            {/* Final CTA */}
            <div className="bg-[radial-gradient(circle_at_bottom,_#FFF4A8_0%,_#FFF9D6_35%,_#FFFCF2_70%,_#FFFFFF_100%)] text-black py-20">
                <div className="container mx-auto px-4 text-center">
                    <h2 className="text-3xl md:text-4xl font-bold mb-6">
                        Let's Build the Future Together
                    </h2>

                    <p className="text-lg md:text-xl max-w-3xl mx-auto mb-10">
                        If you are interested in partnering with a fintech company focused on
                        building compliant and scalable payment infrastructure in India...
                    </p>

                    <div className="text-lg space-y-3 mb-10">
                        <p>
                            Email:
                            <a
                                href="mailto:investors@haodapay.com"
                                className="underline hover:text-[#4E2AAD]"
                            >
                                investors@haodapay.com
                            </a>
                        </p>

                        <p>
                            Toll Free:
                            <a
                                href="tel:18005726367"
                                className="underline hover:text-[#4E2AAD]"
                            >
                                1800-572-6367
                            </a>
                        </p>
                    </div>
                    
                    <a href="#investor-form" className="inline-block">
                    <button className="bg-[#4E2AAD] text-white hover:bg-[#40228F] font-semibold px-10 py-4 rounded-2xl flex items-center gap-3 text-lg mx-auto transition">
                        Contact Us Today <FaArrowRight />
                    </button>
                    </a>
                </div>
            </div>

            <Nextstep />
            <Footer />
        </>
    );
};

export default InvestmentProposal;